import { QueryClient } from '@tanstack/react-query';

import { SIX_HOURS_IN_MILLISECONDS } from '@/constants/common';
import { useGetMagazineRelatedMagazinesQuery } from '@/hooks/magazine/magazine.gql.impl';
import { GetMagazineRelatedMagazinesQuery } from '@/types/bff/gql-schema';
import { MagazineListItemDto, RelatedMagazinesVo } from '@/types/magazine';
import dayjs from 'dayjs';

const parseRelatedMagazines = (data?: GetMagazineRelatedMagazinesQuery): Array<RelatedMagazinesVo> => {
  const parseMagazineListItem = (magazine: MagazineListItemDto) => {
    return {
      ...magazine,
      magazinePid: magazine.magazinePid,
      date: `${dayjs(magazine.createdAt).format('YYYY.MM.DD')}`,
    };
  };

  if (data) {
    return data.magazineRelatedMagazines.map((magazine) => {
      return {
        ...parseMagazineListItem(magazine as MagazineListItemDto),
        contentType: magazine.contentType,
        isArticle: magazine.contentType === 'ARTICLE',
      };
    });
  }

  return [];
};

const useGetRelatedMagazine = (magazinePid: string) => {
  const { data: relatedMagazine = [] } = useGetMagazineRelatedMagazinesQuery(
    {
      request: {
        size: 4,
        magazinePid,
      },
    },
    {
      select: parseRelatedMagazines,
      staleTime: SIX_HOURS_IN_MILLISECONDS,
      cacheTime: SIX_HOURS_IN_MILLISECONDS,
    }
  );
  return {
    relatedMagazine,
  };
};

export default useGetRelatedMagazine;

useGetRelatedMagazine.getKey = (magazinePid: string) => {
  return useGetMagazineRelatedMagazinesQuery.getKey({
    request: {
      size: 4,
      magazinePid,
    },
  });
};

useGetRelatedMagazine.prefetchQuery = async (queryClient: QueryClient, magazinePid: string) => {
  await queryClient.prefetchQuery(
    useGetMagazineRelatedMagazinesQuery.getKey({
      request: {
        magazinePid,
        size: 4,
      },
    }),
    useGetMagazineRelatedMagazinesQuery.fetcher({
      request: {
        magazinePid,
        size: 4,
      },
    })
  );
};
